import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { trackFacebookEvent } from '../utils/facebookPixel';
import { gtmEvents } from '../utils/gtmDataLayer';
import CartItem from '../components/cart/CartItem';
import './Cart.css';

const Cart: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { items, total, itemCount } = useCart();
  const currentLang = i18n.language;

  const shippingCost = 60;
  const finalTotal = total + shippingCost;

  // Track view_cart on page load
  useEffect(() => {
    if (items.length > 0) {
      gtmEvents.viewCart(
        items.map(item => ({
          id: item.product.id,
          name: currentLang === 'bn' ? item.product.name_bn : item.product.name_en,
          price: item.product.final_price,
          quantity: item.quantity,
        })),
        total
      );
    }
  }, [items, total, currentLang]);

  const handleCheckout = () => {
    // Track Facebook InitiateCheckout
    trackFacebookEvent('InitiateCheckout', {
      content_ids: items.map(item => item.product.id.toString()),
      content_type: 'product',
      value: finalTotal,
      currency: 'BDT',
      num_items: itemCount
    });
    
    // Track GTM/GA4 begin_checkout
    gtmEvents.beginCheckout(
      items.map(item => ({
        id: item.product.id,
        name: currentLang === 'bn' ? item.product.name_bn : item.product.name_en,
        price: item.product.final_price,
        quantity: item.quantity,
      })),
      finalTotal
    );
    
    navigate('/checkout');
  };

  return (
    <div className="cart-page">
      <div className="container">
        <h1>{t('cart.title')}</h1>

        {items.length === 0 ? (
          <div className="empty-cart">
            <p>{t('cart.empty')}</p>
            <Link 
              to="/products" 
              className="continue-shopping-btn"
              onClick={() => trackFacebookEvent('Search', { search_string: 'browse_products', content_type: 'product' })}
            >
              {t('cart.continueShopping')}
            </Link>
          </div>
        ) : (
          <div className="cart-layout">
            <div className="cart-items">
              <div className="cart-items-header">
                <span>{t('product.product')}</span>
                <span>{t('product.price')}</span>
                <span>{t('product.quantity')}</span>
                <span>{t('cart.subtotal')}</span>
                <span>{t('cart.remove') || 'Delete'}</span>
              </div>
              
              {items.map((item) => (
                <CartItem key={item.product.id} item={item} />
              ))}
            </div>

            <div className="cart-summary">
              <h3>{t('cart.orderSummary')}</h3>
              
              <div className="summary-row">
                <span>{t('cart.subtotal')} ({itemCount} {t('cart.items')})</span>
                <span>৳{total.toFixed(2)}</span>
              </div>

              <div className="summary-row">
                <span>{t('cart.shipping')}</span>
                <span>৳{shippingCost.toFixed(2)}</span>
              </div>

              <div className="summary-divider"></div>

              <div className="summary-row total-row">
                <span>{t('cart.total')}</span>
                <span>৳{finalTotal.toFixed(2)}</span>
              </div>

              <button 
                className="checkout-btn" 
                onClick={handleCheckout}
              >
                {t('cart.checkout')}
              </button>

              <Link 
                to="/products" 
                className="continue-shopping-link"
                onClick={() => trackFacebookEvent('Search', { search_string: 'browse_products', content_type: 'product' })}
              >
                ← {t('cart.continueShopping')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
